/**
 * Practice Upgrade Modal
 *
 * Shown when prompting users to upgrade to the Practice (PRO_PLUS) plan.
 * Uses HeidiUpgradeBaseModal with variant='practice'.
 *
 * Figma: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=7454-175300
 * Trial-recap feature list: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=10266-38745
 */

import { AppleIapInfoText } from '@/components/apple-iap-info-text';
import { HeidiUpgradeBaseModal } from '@/components/modal/heidi-upgrade-base-modal/heidi-upgrade-base-modal';
import { PriceDisplay } from '@/components/modal/heidi-upgrade-base-modal/price-display';
import { USAGE_POLICY } from '@/components/utils/legalDocuments';
import { useIsHeidi2PricingPlansModalEnabled } from '@/features/flags';
import { UpgradeTeamAccessSvg } from '@/features/settings/billing/components/upgrade-team-access-svg';
import { usePracticeUpgradeModal } from '@/features/settings/billing/hooks/usePracticeUpgradeModal';
import { resolveClinicianTrialRecapTestimonialVariant } from '@/features/trialsAndUpgrades/clinician-trial-recap-testimonial';
import { UpgradeTrialFeatureList } from '@/features/trialsAndUpgrades/ninety-day-end-of-trial/upgrade-trial-feature-list';
import { TeamTrialUpgradeRequestButton } from '@/features/trialsAndUpgrades/team-trial-upgrade-request-button/team-trial-upgrade-request-button';
import { openTeamTrialUpgradeRequestModalAtom } from '@/features/trialsAndUpgrades/team-trial-upgrade-request-modal/atoms';
import { trialRecapModalMessages } from '@/features/trialsAndUpgrades/trial-recap-modal/messages';
import { useRegion } from '@/helpers/getRegion';
import { getTermsOfUseLink } from '@/helpers/localiseHelper';
import { trackUpgradeToPracticeModalTeamUpgradeRequestSubmit } from '@/models/analytics/features/trackTeamTrialUpgradeRequestAnalytics';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import {
  ClinicianTrialRecapTestimonial,
  trialUpgradeRequestCallbackModalMessages,
  UpgradeTermsFooter,
} from '@heidi/commerce';
import { Period } from '@heidi/commerce/contracts';
import { Button } from '@heidi/design-system/button';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { TierPracticeFilled } from '@heidi/design-system/icons';
import { Link } from '@heidi/design-system/link';
import { TypographyH2, TypographyP2 } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useQueryUser } from '@heidi/workspace/data';
import { useSetAtom } from 'jotai';
import { Loader2 } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

import { practiceUpgradeRecapMessages } from './practice-upgrade-recap-messages';

interface PracticeUpgradeModalProps {
  /** When provided, modal is controlled by launch flow */
  launchOpen?: boolean;
  onLaunchClose?: () => void;
}

export const PracticeUpgradeModal = ({
  launchOpen,
  onLaunchClose,
}: PracticeUpgradeModalProps = {}) => {
  const { t } = useTranslations();
  const isHeidi2PricingPlansModalEnabled = useIsHeidi2PricingPlansModalEnabled();
  const {
    isOpen,
    handleOpenChange,
    handleClose,
    handleUpgradeClick,
    handleTalkToUsClick,
    handleExtendTrial,
    handleViewAllPlans,
    isUpgradeDisabled,
    upgradeDisabledTooltip,
    titleText,
    subtitleText,
    effectiveDaysLeft,
    showNinetyDayTrialVariant,
    showNonAdminRequestBanner,
    featuresTitle,
    features,
    practiceProduct,
    isPending,
    showExtendTrialButton,
    footerText,
    showContactSalesInsteadOfUpgrade,
    handleContactSalesClick,
    hasCallbackRequested,
    isFromTrialRecap,
    isNinetyDayTrialEnded,
    planChangesArticleUrl,
  } = usePracticeUpgradeModal({ launchOpen, onLaunchClose });
  const openTeamTrialUpgradeRequestModal = useSetAtom(
    openTeamTrialUpgradeRequestModalAtom
  );
  const intl = useIntl();
  const region = useRegion();
  const { data: user } = useQueryUser();
  const variant = resolveClinicianTrialRecapTestimonialVariant(region, user?.country);
  const termsHref = getTermsOfUseLink(intl.locale);

  return (
    <HeidiUpgradeBaseModal
      variant="practice"
      yearlyOnly
      hideTestimonial={isFromTrialRecap}
      hidePricing={showNonAdminRequestBanner}
      pricingFeaturesGroupedCard={showNinetyDayTrialVariant}
      isUpgradeDisabled={isUpgradeDisabled}
      upgradeDisabledTooltip={upgradeDisabledTooltip}
      title={
        <div className="flex flex-col gap-2">
          {isFromTrialRecap && <FramedIcon state="practice" className="mx-auto mb-2" />}
          <TypographyH2 className="text-text-primary">
            {isFromTrialRecap ? (
              <FormattedMessage {...practiceUpgradeRecapMessages.upgradingTitle} />
            ) : isNinetyDayTrialEnded ? (
              <FormattedMessage
                {...practiceUpgradeRecapMessages.titleNinetyDayTrialEnded}
              />
            ) : showNinetyDayTrialVariant ? (
              <FormattedMessage
                {...practiceUpgradeRecapMessages.title}
                values={{ daysLeft: effectiveDaysLeft }}
              />
            ) : (
              titleText
            )}
          </TypographyH2>
          {!isFromTrialRecap && (
            <TypographyP2 className="text-text-secondary">
              {isNinetyDayTrialEnded ? (
                <FormattedMessage
                  {...practiceUpgradeRecapMessages.subtitleNinetyDayTrialEnded}
                />
              ) : showNinetyDayTrialVariant ? (
                <FormattedMessage
                  {...(showNonAdminRequestBanner
                    ? practiceUpgradeRecapMessages.subtitleNonAdminTeam
                    : practiceUpgradeRecapMessages.subtitle)}
                  values={{
                    link: (chunks) => (
                      <Link
                        variant="inline"
                        tone="default"
                        href={planChangesArticleUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {chunks}
                      </Link>
                    ),
                  }}
                />
              ) : (
                subtitleText
              )}
            </TypographyP2>
          )}
        </div>
      }
      featuresTitle={showNinetyDayTrialVariant ? undefined : featuresTitle}
      features={showNinetyDayTrialVariant ? undefined : features}
      featuresContent={
        showNinetyDayTrialVariant ? (
          <UpgradeTrialFeatureList tier={SubscriptionTier.CLINICIAN_PRO_PLUS} />
        ) : undefined
      }
      yearlyContent={
        practiceProduct ? (
          <PriceDisplay period={Period.ONE_YEAR} product={practiceProduct} />
        ) : null
      }
      rightPanelContent={
        showNinetyDayTrialVariant ? (
          <ClinicianTrialRecapTestimonial variant={variant} />
        ) : (
          <div>
            <UpgradeTeamAccessSvg className="h-auto w-full" />
          </div>
        )
      }
      primaryUpgradeButtonContent={
        <>
          <TierPracticeFilled className="shrink-0" aria-hidden />
          {isPending ? (
            <Loader2 className="animate-spin" />
          ) : isNinetyDayTrialEnded ? (
            t('upgrade.upgradeToPractice')
          ) : showNinetyDayTrialVariant ? (
            <FormattedMessage {...practiceUpgradeRecapMessages.upgradeCta} />
          ) : (
            t('upgrade.upgradeNow')
          )}
        </>
      }
      primaryButtonTestId="billing-practice-upgrade-confirm-button"
      onUpgradeClick={(period) => void handleUpgradeClick(period)}
      secondaryButton={
        showNinetyDayTrialVariant && !showContactSalesInsteadOfUpgrade ? (
          <Button
            variant="outline"
            size="md"
            className="w-full"
            onClick={handleTalkToUsClick}
          >
            {t('pricingPlansModal.talkToUs')}
          </Button>
        ) : showExtendTrialButton ? (
          <Button
            variant="outline"
            size="md"
            className="w-full"
            onClick={handleExtendTrial}
          >
            {t('upgrade.extendYourTrial')}
          </Button>
        ) : isHeidi2PricingPlansModalEnabled ? (
          <Button
            variant="ghost"
            size="md"
            className="w-full"
            onClick={handleViewAllPlans}
          >
            {t('clinicianTrial.upgradeModal.viewAllPlans')}
          </Button>
        ) : undefined
      }
      isUpgradePending={isPending}
      footerText={
        showNonAdminRequestBanner ? undefined : showNinetyDayTrialVariant ? (
          <UpgradeTermsFooter termsHref={termsHref} privacyHref={USAGE_POLICY} />
        ) : (
          footerText
        )
      }
      footerCaptionClassName={
        showNonAdminRequestBanner
          ? undefined
          : showNinetyDayTrialVariant
            ? 'text-center'
            : undefined
      }
      primaryCtaReplacement={
        isAppleCompliantBuild()
          ? () => <AppleIapInfoText className="text-center text-text-secondary" />
          : showNonAdminRequestBanner
            ? (assignPrimaryButtonRef) => (
                <TeamTrialUpgradeRequestButton
                  ref={(el) => {
                    assignPrimaryButtonRef(el);
                  }}
                  plan={SubscriptionTier.CLINICIAN_PRO_PLUS}
                  onOpenTeamRequestModal={() => {
                    handleClose();
                    openTeamTrialUpgradeRequestModal({
                      plan: SubscriptionTier.CLINICIAN_PRO_PLUS,
                      trackRequestSubmitted:
                        trackUpgradeToPracticeModalTeamUpgradeRequestSubmit,
                      onRequestSent: () => {
                        handleClose();
                      },
                      onUserDismiss: () => {
                        handleOpenChange(true);
                      },
                    });
                  }}
                />
              )
            : showContactSalesInsteadOfUpgrade
              ? (assignPrimaryButtonRef) => (
                  <Button
                    ref={(el) => {
                      assignPrimaryButtonRef(el);
                    }}
                    type="button"
                    variant="pro_plus"
                    size="md"
                    disabled={hasCallbackRequested}
                    onClick={handleContactSalesClick}
                    className="w-full"
                  >
                    {!hasCallbackRequested && (
                      <TierPracticeFilled className="shrink-0" aria-hidden />
                    )}
                    <FormattedMessage
                      {...(hasCallbackRequested
                        ? trialUpgradeRequestCallbackModalMessages.callbackRequestedCta
                        : trialRecapModalMessages.talkToUsKeepPlanCtaPractice)}
                    />
                  </Button>
                )
              : undefined
      }
      open={isOpen}
      onOpenChange={handleOpenChange}
    />
  );
};
